/**
 * The address bar, read and written: every page has a path of its own, so a
 * reload lands where it was and the browser's Back and Forward walk the pages
 * that were seen (Claude Design « Account » handoff, README §2 bis).
 *
 * What a path names is the shell's place — page, section, organization,
 * workspace, team, the inspected account or organization. Modals, menus and
 * drafts stay out of it, and so does anything secret. An organization is named
 * by its slug, a workspace, a team and an inspected row by their id, since only
 * the organization's slug is known before its pages have read anything.
 */
import type { PageId } from '../components/nav';
import type { Organization } from './types';

export interface Route {
  page: PageId;
  section: string | null;
  /** `undefined` keeps the organization the shell is on (the identity menu's pages belong to none). */
  orgId?: string | null;
  inWorkspace: boolean;
  workspaceId: string | null;
  teamId: string | null;
  /** The account or organization a system administrator is looking at. */
  inspecting: string | null;
}

/** The org pages, by the segment that names them under `/orgs/{slug}/`. */
const ORG_SEGMENTS: [PageId, string][] = [
  ['workspaces', 'workspaces'],
  ['teams', 'teams'],
  ['members', 'members'],
  ['orgMachines', 'machines'],
  ['audit', 'audit'],
  ['usage', 'statistics'],
  ['runs', 'runs'],
  ['spend', 'spend'],
  ['streams', 'streaming'],
  ['billing', 'billing'],
  ['orgSettings', 'settings'],
  ['orgWorkflows', 'workflows'],
  ['orgRules', 'rules'],
  ['orgAgents', 'agents'],
  ['orgIntegrations', 'integrations'],
  ['orgSecrets', 'secrets'],
  ['orgSecurity', 'security']
];

const WS_SEGMENTS: [PageId, string][] = [
  ['wsProjects', 'projects'],
  ['wsAccess', 'access'],
  ['wsIntegrations', 'integrations'],
  ['wsSecrets', 'secrets'],
  ['wsHistory', 'history'],
  ['wsSettings', 'settings'],
  ['wsAgents', 'agents'],
  ['wsRules', 'rules']
];

/** Pages that are the same wherever they are opened from: the identity menu, and a personal account's own. */
const TOP_SEGMENTS: [PageId, string][] = [
  ['machines', 'machines'],
  ['downloads', 'downloads'],
  ['profile', 'profile'],
  ['affiliate', 'affiliate'],
  ['overview', 'overview'],
  ['licence', 'licence'],
  ['invoices', 'invoices'],
  ['usage', 'statistics']
];

const segmentOf = (table: [PageId, string][], page: PageId): string | undefined =>
  table.find(([one]) => one === page)?.[1];
const pageOf = (table: [PageId, string][], segment: string | undefined): PageId | undefined =>
  table.find(([, one]) => one === segment)?.[0];
const part = (value: string): string => encodeURIComponent(value);

/** The path a place is written as. */
export function pathOf(route: Route, orgs: readonly Organization[]): string {
  const { page, section } = route;
  const withSection = (base: string): string => (section ? `${base}/${part(section)}` : base);
  if (page === 'machines' || page === 'downloads' || page === 'profile' || page === 'affiliate') return `/${part(page)}`;
  if (page.startsWith('admin')) {
    if (page === 'adminAccounts') return '/admin/accounts';
    if (page === 'adminAccountDetail') return route.inspecting ? `/admin/accounts/${part(route.inspecting)}` : '/admin/accounts';
    if (page === 'adminOrgs') return '/admin/organizations';
    if (page === 'adminAffiliates') return '/admin/affiliates';
    if (page === 'adminRequests') return '/admin/requests';
    if (page === 'adminOrgDetail') return route.inspecting ? `/admin/organizations/${part(route.inspecting)}` : '/admin/organizations';
    return '/admin';
  }
  const org = orgs.find((one) => one.id === route.orgId);
  if (!org) return `/${part(segmentOf(TOP_SEGMENTS, page) ?? 'overview')}`;
  const base = `/orgs/${part(org.slug)}`;
  if (route.inWorkspace) {
    const ws = `${base}/workspaces/${part(route.workspaceId || 'new')}`;
    return withSection(`${ws}/${segmentOf(WS_SEGMENTS, page) ?? 'projects'}`);
  }
  if (page === 'overview') return base;
  if (page === 'team') return route.teamId ? `${base}/teams/${part(route.teamId)}` : `${base}/teams`;
  const segment = segmentOf(ORG_SEGMENTS, page);
  return segment ? withSection(`${base}/${part(segment)}`) : base;
}

/** The place a path names, or `null` when it names none this account can reach. */
export function routeOf(pathname: string, orgs: readonly Organization[]): Route | null {
  const parts = pathname
    .split('/')
    .filter(Boolean)
    .map((one) => decodeURIComponent(one));
  const blank: Route = { page: 'overview', section: null, inWorkspace: false, workspaceId: null, teamId: null, inspecting: null };
  if (parts.length === 0) return null;
  const [head, second, third, fourth, fifth] = parts;
  if (head === 'admin') {
    if (second === 'accounts') return { ...blank, page: third ? 'adminAccountDetail' : 'adminAccounts', inspecting: third ?? null };
    if (second === 'organizations') return { ...blank, page: third ? 'adminOrgDetail' : 'adminOrgs', inspecting: third ?? null };
    if (second === 'affiliates') return { ...blank, page: 'adminAffiliates' };
    if (second === 'requests') return { ...blank, page: 'adminRequests' };
    return { ...blank, page: 'adminOverview' };
  }
  if (head === 'orgs') {
    const org = orgs.find((one) => one.slug === second);
    if (!org) return null;
    const at = { ...blank, orgId: org.id };
    if (!third) return at;
    if (third === 'workspaces' && fourth) {
      const page = pageOf(WS_SEGMENTS, fifth) ?? 'wsProjects';
      return { ...at, page, section: parts[5] ?? null, inWorkspace: true, workspaceId: fourth === 'new' ? null : fourth };
    }
    if (third === 'teams' && fourth) return { ...at, page: 'team', teamId: fourth };
    const page = pageOf(ORG_SEGMENTS, third);
    return page ? { ...at, page, section: fourth ?? null } : null;
  }
  const page = pageOf(TOP_SEGMENTS, head);
  if (!page) return null;
  // The identity menu's pages keep whichever organization the shell is on; a
  // personal account's own pages are the shell with no organization.
  return page === 'machines' || page === 'downloads' || page === 'profile' || page === 'affiliate'
    ? { ...blank, page, orgId: undefined }
    : { ...blank, page, orgId: null };
}
